import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { XSRF_TOKEN_SOURCE } from '@webapp/platform/xsrf/xsrf-token-source.port';

/**
 * Distinct marker for the client-side rejection thrown when a CSRF-protected
 * route is called without a readable XSRF token. Consumers (see
 * my-profile-page's handleRevokeError) must check for this shape BEFORE
 * falling back to HTTP-status-based error handling, since this rejection
 * never reaches the network.
 */
export interface CsrfMissingError {
  readonly csrfMissing: true;
  readonly message: string;
}

const XSRF_HEADER_NAME = 'X-XSRF-TOKEN';

interface XsrfProtectedRoute {
  method: string;
  pattern: RegExp;
}

/**
 * Route-scoped: only the session-revoke and API keys (create/delete)
 * endpoints require CSRF echo.
 * Angular's built-in XSRF protection is disabled (see D1) because our API
 * is cross-origin and `HttpXsrfInterceptor` only attaches the header for
 * same-origin/relative URLs.
 */
const XSRF_PROTECTED_ROUTES: XsrfProtectedRoute[] = [
  { method: 'DELETE', pattern: /\/auth\/sessions\/[^/]+\/?$/ },
  { method: 'POST', pattern: /\/auth\/sessions\/revoke-others\/?$/ },
  { method: 'POST', pattern: /\/users\/me\/api-keys\/?$/ },
  { method: 'DELETE', pattern: /\/users\/me\/api-keys\/[^/]+\/?$/ },
];

const requiresXsrfToken = (req: HttpRequest<unknown>): boolean =>
  XSRF_PROTECTED_ROUTES.some(
    ({ method, pattern }) => req.method === method && pattern.test(req.url)
  );

export const xsrfInterceptor: HttpInterceptorFn = (req, next) => {
  if (!requiresXsrfToken(req)) {
    return next(req);
  }

  // Pluggable source (design D1/D5): web reads document.cookie, native reads
  // an in-memory value captured from the sign-in/refresh response body. See
  // libs/platform/src/lib/xsrf/xsrf-token-source.port.ts.
  const xsrfToken = inject(XSRF_TOKEN_SOURCE).read();

  if (!xsrfToken) {
    const csrfMissingError: CsrfMissingError = {
      csrfMissing: true,
      message: 'XSRF-TOKEN cookie missing; request blocked before being sent.',
    };
    return throwError(() => csrfMissingError);
  }

  return next(
    req.clone({
      headers: req.headers.set(XSRF_HEADER_NAME, xsrfToken),
    })
  );
};
